/**
 * @file パララックス効果モジュール（GSAP ScrollTrigger対応）
 * パララックス効果: 背景デコレーションとテキストブロックを異なる速度で移動
 * スムーススクロール: GSAP scrubで滑らかなスクロール演出
 * 対象要素:
 * - KVコンテンツ: .p-topKvVideo__content（上方向移動 + フェード）
 * - KVニュース: .p-topKv__news（上方向移動）
 * - 背景デコレーション: .p-topMessage__bgDecor-img01〜04（個別パララックス）
 * - テキストブロック: .p-topMessage .l-block（パララックス）
 * @constructor
*/
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const DEBUG = false; // デバッグモード: true でScrollTriggerのマーカーを表示

// 画面上にデバッグ情報を表示するユーティリティ（実機確認用）
// eslint-disable-next-line no-unused-vars
const dbg = () => {}; // デバッグ無効時はno-op

export default class Parallax {
  constructor() {
    this.mm = gsap.matchMedia();
    dbg('[1] Parallax constructor 実行された');
    const run = () => {
      dbg('[2] run() 実行: readyState=' + document.readyState);
      this.init();
      ScrollTrigger.refresh();
    };
    // load済みなら即実行（type="module"スクリプトの遅延実行対策）
    if (document.readyState === 'complete') {
      dbg('[1b] 即実行パス');
      run();
    } else {
      dbg('[1b] load待機パス');
      window.addEventListener('load', run, { once: true });
    }
  }

  init() {
    dbg('[3] init() 開始');
    if (DEBUG) ScrollTrigger.defaults({ markers: true });

    // スマホ動的ビューポート対策: --vh を JS で管理（アドレスバーの出死による100vhジャンプ防止）
    const setVh = () => document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    setVh();
    window.addEventListener('resize', setVh);

    // FOUC防止: .p-topMessage 外の .js-fadein 系要素のみ opacity:0（.p-topMessage 内は initTopMessageParallax 内の gsap.set で管理）
    const fouckEls = Array.from(document.querySelectorAll('.js-fadein, .js-fadein-delay'))
      .filter(el => !el.closest('.p-topMessage'));
    if (fouckEls.length) gsap.set(fouckEls, { opacity: 0 });

    try { this.initTopMessageParallax(); } catch(e) { dbg('[ERR] initTopMessageParallax: ' + e.message); }
    dbg('[3a] initTopMessageParallax 完了');
    try { this.initTopKv(); } catch(e) { dbg('[ERR] initTopKv: ' + e.message); }
    dbg('[3b] initTopKv 完了');
    try { this.initFadeins(); } catch(e) { dbg('[ERR] initFadeins: ' + e.message); }
    dbg('[4] 全初期化完了 ST数=' + ScrollTrigger.getAll().length);
  }

  initTopKv() {
    dbg('[initTopKv] 開始');
    try {
    const wrapper = document.querySelector('.p-topKvWrapper');
    if (!wrapper) return;

    const video = wrapper.querySelector('.p-topKvVideo__video');
    const overlay = wrapper.querySelector('.p-topKvOverlay'); // contentOuter + news をまとめたラッパー

    // 動画やポスターを画面幅に応じて切り替え（PC / SP）
    const pcSrc = video?.dataset?.srcPc || '/assets/video/kv_movie_pc.mp4';
    const spSrc = video?.dataset?.srcSp || '/assets/video/kv_movie_sp.mp4';
    const pcPoster = video?.dataset?.posterPc || '/assets/img/top/kv_poster_pc.webp';
    const spPoster = video?.dataset?.posterSp || '/assets/img/top/kv_poster_sp.webp';

    // PC でもブラウザズームなどで CSSピクセル幅が 750 以下になってしまうと
    // matchMedia('(max-width:750px)') が true になるため、ユーザーエージェント
    // から「モバイルかどうか」を判定しておく。
    const ua = window.navigator.userAgent.toLowerCase();
    const isMobileUA = /iphone|ipod|android.*mobile|windows phone/.test(ua);

    const setVideoSrc = ({ src, poster }) => {
      if (!video) return;
      if (video.getAttribute('data-current-src') === src && video.getAttribute('data-current-poster') === poster) return;
      if (src) {
        video.setAttribute('data-current-src', src);
        video.src = src;
      }
      if (poster) {
        video.setAttribute('data-current-poster', poster);
        video.poster = poster;
      }
      // 再読み込みして autoplay を確実に動かす
      video.load();
    };

    [wrapper, video].filter(Boolean).forEach(el => gsap.set(el, { clearProps: 'height,maxHeight' }));
    document.querySelectorAll('.pin-spacer').forEach(sp => gsap.set(sp, { clearProps: 'height' }));

    if (isMobileUA) {
      // スマホ: 画面幅で SP / PC の動画とアニメーションを切り替え
      this.mm.add('(max-width: 750px)', () => {
        setVideoSrc({ src: spSrc, poster: spPoster });
        gsap.set([wrapper, video].filter(Boolean), { height: 'calc(var(--vh, 1vh) * 85)' });
        // overlay は GSAP で動かさない → CSS sticky のビデオの上を自然スクロール
        if (overlay) gsap.set(overlay, { clearProps: 'all' });

        // スクロール中盤〜後半でフェードアウト
        gsap.to('.p-topKvVideo', {
          opacity: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: wrapper,
            start: 'top+=25% top',
            end: 'top+=70% top',
            scrub: true,
          },
        });

        return () => {
          gsap.set('.p-topKvVideo', { clearProps: 'opacity' });
          if (overlay) gsap.set(overlay, { clearProps: 'all' });
          gsap.set([wrapper, video].filter(Boolean), { clearProps: 'height,maxHeight' });
        };
      });

      this.mm.add('(min-width: 751px)', () => {
        setVideoSrc({ src: pcSrc, poster: pcPoster });
        dbg('[KV-PC] matchMedia 発火');
        if (overlay) gsap.set(overlay, { y: 0 });
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: wrapper,
            start: 'top top',
            end: '+=24%',
            scrub: 0,
            pin: '.p-topKvVideo',
            anticipatePin: 1,
          },
        });
        tl.to(video, { scale: 1, ease: 'none', duration: 1.4 }, 0);
        tl.to(video, { opacity: 0, ease: 'none', duration: 0.8 }, 0.4);
        if (overlay) {
          const overlayShift = Math.round(Math.min(window.innerHeight * 0.12, 200));
          tl.to(overlay, { y: -overlayShift, ease: 'none', duration: 1.2 }, 0);
        }
        return () => {
          tl.kill();
          if (overlay) gsap.set(overlay, { clearProps: 'transform' });
          gsap.set(video, { clearProps: 'opacity' });
          gsap.set([wrapper, video].filter(Boolean), { clearProps: 'height,maxHeight' });
        };
      });
    } else {
      // デスクトップ: 常に PC用動画 + PC スクロールアニメーション
      setVideoSrc({ src: pcSrc, poster: pcPoster });
      dbg('[KV-PC] デスクトップ固定');
      if (overlay) gsap.set(overlay, { y: 0 });
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper,
          start: 'top top',
          end: '+=24%',
          scrub: 0,
          pin: '.p-topKvVideo',
          anticipatePin: 1,
        },
      });
      tl.to(video, { scale: 1, ease: 'none', duration: 1.4 }, 0);
      // オーバーレイではなく video 自体を透明にしていく
      tl.to(video, { opacity: 0, ease: 'none', duration: 0.8 }, 0.4);
      if (overlay) {
        const overlayShift = Math.round(Math.min(window.innerHeight * 0.12, 200));
        tl.to(overlay, { y: -overlayShift, ease: 'none', duration: 1.2 }, 0);
      }
    }
    dbg('[initTopKv] 登録完了');
    } catch(e) { dbg('[initTopKv] エラー: ' + e.message); }
  }

  initTopMessageParallax() {
    const section = document.querySelector('.p-topMessage');
    if (!section) return;

    const textBlock = section.querySelector('.l-block');
    const titleEl = section.querySelector('.p-topMessage__title');
    const textWrapper = section.querySelector('.p-topMessage__textWrapper');
    const buttonWrapper = section.querySelector('.p-topMessage__buttonWrapper');

    const img01 = section.querySelector('.p-topMessage__bgDecor-img01');
    const img02 = section.querySelector('.p-topMessage__bgDecor-img02');
    const img03 = section.querySelector('.p-topMessage__bgDecor-img03');
    const img04 = section.querySelector('.p-topMessage__bgDecor-img04');

    this.mm.add('(max-width: 750px)', () => {
        // y: scrub で滑らかに双方向連動 / opacity: once で一度フェードインしたら戻さない
        const createLagEntrance = (el, fromY, toY, start, lag = 0.6) => {
          if (!el) return;
          gsap.set(el, { y: fromY, opacity: 0 });
          ScrollTrigger.create({
            trigger: el,
            start,
            once: true,
            onEnter: () => gsap.to(el, { opacity: 1, duration: 0.6, ease: 'power2.out' }),
          });
          gsap.fromTo(el,
            { y: fromY },
            {
              y: toY,
              ease: 'none',
              scrollTrigger: {
                trigger: el,
                start,
                end: 'bottom top',
                scrub: lag,
              },
            }
          );
        };

        if (titleEl) createLagEntrance(titleEl, 120, 0, 'top 85%', 1.6);
        if (textWrapper) createLagEntrance(textWrapper, 160, 0, 'top 78%', 1.8);

        // ボタン: scrub で y 追従 + once で opacity フェード
        if (buttonWrapper) {
          gsap.set(buttonWrapper, { y: 160, opacity: 0 });
          ScrollTrigger.create({
            trigger: buttonWrapper,
            start: 'top 92%',
            once: true,
            onEnter: () => gsap.to(buttonWrapper, { opacity: 1, duration: 0.9, ease: 'power2.out' }),
          });
          gsap.fromTo(buttonWrapper,
            { y: 160 },
            {
              y: 0,
              ease: 'none',
              scrollTrigger: {
                trigger: textWrapper,
                start: 'top 78%',
                end: 'bottom top',
                scrub: 1.8,
              },
            }
          );
        }
        const createLagParallax = (el, fromY, toY, lag = 0.7) => {
          if (!el) return;
          gsap.set(el, { y: fromY, opacity: 0 });

          // opacity: 一度フェードインしたら戻さない（スクロールバックで消えない）
          ScrollTrigger.create({
            trigger: titleEl,
            start: 'top 65%',
            once: true,
            onEnter: () => gsap.to(el, { opacity: 0.8, duration: 0.8, ease: 'power2.out' }),
          });

          // y: scrub で滑らかに双方向連動
          gsap.fromTo(el,
            { y: fromY },
            {
              y: toY,
              ease: 'none',
              scrollTrigger: {
                trigger: titleEl,
                start: 'top 65%',
                end: 'bottom top',
                scrub: lag,
              },
            }
          );
        };

        createLagParallax(img01, 100, -15, 1.8);
        createLagParallax(img02, 120, -25, 2.0);
        createLagParallax(img03, 110, -25, 2.4);
        createLagParallax(img04, 140, -15, 2.2);

        if (textBlock) {
          gsap.fromTo(textBlock,
            { y: 60 },
            {
              y: -20,
              ease: 'none',
              duration: 1,
              immediateRender: false,
              scrollTrigger: {
                trigger: section,
                start: 'top bottom-=80',
                end: 'bottom top',
                scrub: 0.8,
              },
            }
          );
        }
    });

    this.mm.add('(min-width: 751px)', () => {
        const createLagEntranceDesktop = (el, fromY, toY, start, end, lag = 0.5) => {
          if (!el) return;
          gsap.set(el, { y: fromY, opacity: 0 });
          ScrollTrigger.create({
            trigger: el,
            start,
            end,
            onUpdate(self) {
              const target = fromY + (toY - fromY) * self.progress;
              // opacity: progress 値に直結・即時反映でスクロール速度に左右されない
              gsap.set(el, { opacity: Math.min(1, self.progress / 0.2) });
              gsap.to(el, { y: target, duration: lag, ease: 'power3.out', overwrite: true });
            },
            onRefresh() { gsap.set(el, { y: fromY, opacity: 0 }); }
          });
        };

        if (titleEl) createLagEntranceDesktop(titleEl, 140, 0, 'top 80%', 'bottom top', 1.4);
        if (textWrapper) createLagEntranceDesktop(textWrapper, 180, 0, 'top 72%', 'bottom top', 1.6);

        // ボタン: textWrapper と同じ y アニメで一緯に動かす（間隔が開かない）＋ opacity は別途フェード
        if (buttonWrapper) {
          gsap.set(buttonWrapper, { y: 180, opacity: 0 });
          ScrollTrigger.create({
            trigger: textWrapper,
            start: 'top 72%',
            end: 'bottom top',
            onUpdate(self) {
              const target = 180 + (0 - 180) * self.progress;
              gsap.to(buttonWrapper, { y: target, duration: 1.6, ease: 'power3.out', overwrite: 'auto' });
            },
            onRefresh() { gsap.set(buttonWrapper, { y: 180 }); }
          });
          gsap.to(buttonWrapper, {
            opacity: 1,
            duration: 0.9,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: buttonWrapper,
              start: 'top 88%',
              toggleActions: 'play none none reverse',
            },
          });
        }
        const createLagParallaxDesktop = (el, fromY, toY, lag = 0.6) => {
          if (!el) return;
          gsap.set(el, { y: fromY, opacity: 0 });
          ScrollTrigger.create({
            trigger: titleEl,
            start: 'top 30%',
            end: 'bottom top',
            onUpdate(self) {
              const target = fromY + (toY - fromY) * self.progress;
              gsap.set(el, { opacity: Math.min(0.8, (self.progress / 0.2) * 0.8) });
              gsap.to(el, { y: target, duration: lag, ease: 'power3.out', overwrite: true });
            },
            onRefresh() { gsap.set(el, { y: fromY, opacity: 0 }); }
          });
        };

        createLagParallaxDesktop(img01, 300, -120, 1.0);
        createLagParallaxDesktop(img02, 360, -130, 1.0);
        createLagParallaxDesktop(img03, 420, -120, 1.0);
        createLagParallaxDesktop(img04, 480, -130, 1.0);

        if (textBlock) {
          gsap.fromTo(textBlock,
            { y: 60 },
            {
              y: -20,
              ease: 'none',
              duration: 1,
              immediateRender: false,
              scrollTrigger: {
                trigger: section,
                start: 'top bottom-=80',
                end: 'bottom top',
                scrub: 0,
              },
            }
          );
        }
    });
  }

  initFadeins() {
    const fadeinEls = Array.from(document.querySelectorAll('.js-fadein')).filter(el => !el.closest('.p-topMessage'));
    const fadeinDelayEls = Array.from(document.querySelectorAll('.js-fadein-delay')).filter(el => !el.closest('.p-topMessage'));

    fadeinEls.forEach(el => {
      gsap.fromTo(el,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top bottom-=180px',
            toggleActions: 'play none none reverse',
          },
        }
      );
    });

    fadeinDelayEls.forEach(el => {
      gsap.fromTo(el,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top bottom-=50px',
            toggleActions: 'play none none reverse',
          },
        }
      );
    });
  }
}